import { AcrCategory, AcrStatus, type StudyAcrEntrySelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { ConfirmDeleteModal, ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import { useDeleteMeiaAcrEntry, useGetMeiaAcrRegister } from '@queries/meia.query';
import { EditIcon, Grid3x3Icon, PlusIcon, ShieldAlertIcon, Trash2Icon } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { MeiaAcrEntryForm } from './meia-acr-entry-form.component';
import {
  ACR_CATEGORY_ORDER,
  ACR_STATUS_ORDER,
  MEIA_ACR_ENTRY_FORM_ID,
  getAcrCategoryBadgeVariant,
  getAcrCategoryLetter,
  getAcrCategoryTooltip,
  getAcrG4Blocked,
  getAcrImpactLabel,
  getAcrProbabilityLabel,
  getAcrSeverityBadgeVariant,
  getAcrSeverityLabel,
  getAcrStatusBadgeVariant,
  getAcrStatusLabel,
} from './meia-acr.util';

type MeiaAcrRegisterModalProps = {
  canEdit: boolean;
  focusEntryId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  studyId: string;
};

type FormState = StudyAcrEntrySelectDto | 'new' | null;

type TypeFilter = AcrCategory | 'all';
type StatusFilter = AcrStatus | 'all';

type FilterChipProps = {
  active: boolean;
  label: string;
  onClick: () => void;
};

const FilterChip: FC<FilterChipProps> = (props) => {
  const { active, label, onClick } = props;

  return (
    <button
      className={cn(
        'h-6 rounded border px-2 text-xs transition-colors',
        active ? 'border-purple-200 bg-purple-100 text-purple-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50',
      )}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
};

type MeiaAcrEntryCardProps = {
  canEdit: boolean;
  entry: StudyAcrEntrySelectDto;
  isFocused: boolean;
  onDelete: () => void;
  onEdit: () => void;
};

const MeiaAcrEntryCard: FC<MeiaAcrEntryCardProps> = (props) => {
  const { canEdit, entry, isFocused, onDelete, onEdit } = props;
  const isRisk = entry.category === AcrCategory.RISK;
  const showMitigation = Boolean(entry.mitigationApproach) || isRisk;
  const cardRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);

  // When opened from the matrix, scroll this entry into view and pulse a highlight.
  useEffect(() => {
    if (!isFocused) return;
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlighted(true);
    const timer = setTimeout(() => setHighlighted(false), 2200);
    return () => clearTimeout(timer);
  }, [isFocused]);

  return (
    <div
      className={cn(
        'rounded-md border bg-white p-3 transition-all',
        highlighted ? 'border-purple-300 ring-2 ring-purple-300' : 'border-gray-200',
      )}
      ref={cardRef}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <TooltipComponent
            side="top"
            trigger={
              <span className="inline-flex cursor-help">
                <Badge
                  size="xs"
                  variant={getAcrCategoryBadgeVariant(entry.category)}
                >
                  {getAcrCategoryLetter(entry.category)}
                </Badge>
              </span>
            }
          >
            {getAcrCategoryTooltip(entry.category)}
          </TooltipComponent>
          <Badge
            size="xs"
            variant={getAcrStatusBadgeVariant(entry.status)}
          >
            {getAcrStatusLabel(entry.status)}
          </Badge>
          <span className="font-mono text-[11px] text-gray-400">{entry.acrCode}</span>
        </div>
        {canEdit && (
          <div className="flex shrink-0 items-center gap-1">
            <IconButton
              ariaLabel="Edit entry"
              icon={<EditIcon />}
              onClick={onEdit}
              size="sm"
              variant="text"
            />
            <IconButton
              ariaLabel="Delete entry"
              icon={<Trash2Icon />}
              onClick={onDelete}
              size="sm"
              variant="destructive"
            />
          </div>
        )}
      </div>
      {entry.title && <p className="mt-2 text-sm font-medium text-gray-900">{entry.title}</p>}
      <p className="mt-0.5 text-sm text-gray-800">{entry.statement}</p>
      <p className="mt-1 text-xs text-gray-600">
        <span className="font-medium text-gray-500">Rationale: </span>
        {entry.rationale}
      </p>
      {isRisk && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-medium text-gray-500">Likelihood</span>
          <Badge
            size="xs"
            variant="outline"
          >
            {getAcrProbabilityLabel(entry.likelihood)}
          </Badge>
          <span className="font-medium text-gray-500">Impact</span>
          <Badge
            size="xs"
            variant={getAcrSeverityBadgeVariant(entry.impact)}
          >
            {getAcrSeverityLabel(entry.impact)}
          </Badge>
        </div>
      )}
      {!isRisk && entry.potentialImpact && (
        <p className="mt-1.5 text-xs text-gray-600">
          <span className="font-medium text-gray-500">{getAcrImpactLabel(entry.category)}: </span>
          {entry.potentialImpact}
        </p>
      )}
      {showMitigation && (
        <p className="mt-1.5 text-xs text-gray-600">
          <span className="font-medium text-gray-500">Mitigation: </span>
          {entry.mitigationApproach ? (
            entry.mitigationApproach
          ) : (
            <span className="italic text-red-600">— missing —</span>
          )}
        </p>
      )}
      {entry.owner?.profile?.fullName && (
        <p className="mt-2 text-[11px] text-gray-400">Owner: {entry.owner.profile.fullName}</p>
      )}
    </div>
  );
};

export const MeiaAcrRegisterModal: FC<MeiaAcrRegisterModalProps> = (props) => {
  const { canEdit, focusEntryId, isOpen, onClose, studyId } = props;
  const { data: entries = [], isLoading } = useGetMeiaAcrRegister({ studyId: isOpen ? studyId : undefined });
  const { mutateAsync: deleteEntry } = useDeleteMeiaAcrEntry();

  const [formState, setFormState] = useState<FormState>(null);
  const [deletingEntry, setDeletingEntry] = useState<StudyAcrEntrySelectDto | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isFormPending, setIsFormPending] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [pendingClose, setPendingClose] = useState<'form' | 'modal' | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fully reset transient state when the modal closes, so reopening always lands on the entry list.
  useEffect(() => {
    if (isOpen) return;
    setFormState(null);
    setDeletingEntry(null);
    setTypeFilter('all');
    setStatusFilter('all');
    setIsFormPending(false);
    setIsFormDirty(false);
    setPendingClose(null);
    setPendingFocusId(null);
  }, [isOpen]);

  // Arm the matrix-driven focus once per open; the matching card consumes it (scroll + highlight) on mount.
  useEffect(() => {
    if (isOpen) setPendingFocusId(focusEntryId ?? null);
  }, [focusEntryId, isOpen]);

  // Once any form opens, drop the pending focus so returning to the list (e.g. Cancel) does not re-highlight.
  useEffect(() => {
    if (formState) setPendingFocusId(null);
  }, [formState]);

  const isBlocked = getAcrG4Blocked(entries);
  const isFormActive = formState !== null;
  const editingEntry = formState !== null && formState !== 'new' ? formState : null;
  const visibleEntries = entries.filter(
    (entry) =>
      (typeFilter === 'all' || entry.category === typeFilter) &&
      (statusFilter === 'all' || entry.status === statusFilter),
  );

  const handleViewMatrix = (): void => {
    navigate(meiaRoute.studyAcrMatrix(studyId));
    onClose();
  };

  // Close guards (form AGENTS "Close Guards"): every path out of a dirty entry form confirms before discarding.
  const closeForm = (): void => {
    setFormState(null);
    setIsFormDirty(false);
  };

  const requestFormCancel = (): void => {
    if (isFormDirty) {
      setPendingClose('form');
      return;
    }
    closeForm();
  };

  const requestModalClose = (): void => {
    if (isFormActive && isFormDirty) {
      setPendingClose('modal');
      return;
    }
    onClose();
  };

  const confirmDiscard = (): void => {
    const target = pendingClose;
    setPendingClose(null);
    if (target === 'modal') {
      onClose();
      return;
    }
    closeForm();
  };

  return (
    <>
      <Modal
        footer={
          isFormActive
            ? {
                leftButton: { children: 'Cancel', onClick: requestFormCancel, type: 'button' },
                rightButton: {
                  children: editingEntry ? 'Save Changes' : 'Create Entry',
                  disabled: isFormPending,
                  form: MEIA_ACR_ENTRY_FORM_ID,
                  loading: isFormPending,
                  type: 'submit',
                },
              }
            : undefined
        }
        header={{
          actions: isFormActive ? undefined : (
            <Button
              onClick={handleViewMatrix}
              size="sm"
              variant="secondary"
            >
              <Grid3x3Icon className="size-4" />
              Matrix
            </Button>
          ),
          title: isFormActive ? (editingEntry ? `Edit ${editingEntry.acrCode}` : 'New ACR entry') : 'ACR Register',
        }}
        isOpen={isOpen}
        onClose={requestModalClose}
        width="880px"
      >
        {isFormActive ? (
          <MeiaAcrEntryForm
            entry={editingEntry ?? undefined}
            key={editingEntry ? editingEntry.id : 'new'}
            onClose={closeForm}
            onDirtyChange={setIsFormDirty}
            onPendingChange={setIsFormPending}
            studyId={studyId}
          />
        ) : (
          <>
            {isBlocked && (
              <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                <ShieldAlertIcon className="mt-0.5 size-3.5 shrink-0" />
                <span>Decision-maker (G4) sign-off is blocked: an open Risk has no mitigation.</span>
              </div>
            )}

            <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-gray-500">Type</span>
                  <FilterChip
                    active={typeFilter === 'all'}
                    label="All"
                    onClick={() => setTypeFilter('all')}
                  />
                  {ACR_CATEGORY_ORDER.map((category) => (
                    <FilterChip
                      active={typeFilter === category}
                      key={category}
                      label={getAcrCategoryLetter(category)}
                      onClick={() => setTypeFilter(category)}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-gray-500">Status</span>
                  <FilterChip
                    active={statusFilter === 'all'}
                    label="All"
                    onClick={() => setStatusFilter('all')}
                  />
                  {ACR_STATUS_ORDER.map((status) => (
                    <FilterChip
                      active={statusFilter === status}
                      key={status}
                      label={getAcrStatusLabel(status)}
                      onClick={() => setStatusFilter(status)}
                    />
                  ))}
                </div>
              </div>
              {canEdit && (
                <Button
                  className="shrink-0"
                  onClick={() => setFormState('new')}
                  variant="link"
                >
                  <PlusIcon className="size-4" />
                  Add Entry
                </Button>
              )}
            </div>

            {isLoading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : entries.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                No assumptions, constraints, or risks recorded yet.
              </p>
            ) : visibleEntries.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                No entries match the current filters.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {visibleEntries.map((entry) => (
                  <MeiaAcrEntryCard
                    canEdit={canEdit}
                    entry={entry}
                    isFocused={entry.id === pendingFocusId}
                    key={entry.id}
                    onDelete={() => setDeletingEntry(entry)}
                    onEdit={() => setFormState(entry)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={deletingEntry !== null}
        itemName={deletingEntry ? `ACR entry ${deletingEntry.acrCode}` : 'ACR entry'}
        onConfirm={async () => {
          if (!deletingEntry) return;
          await deleteEntry({ acrEntryId: deletingEntry.id, studyId });
          setDeletingEntry(null);
        }}
        setIsOpen={(open) => {
          if (!open) setDeletingEntry(null);
        }}
      />

      <ConfirmationModal
        cancelLabel="Keep Editing"
        confirmLabel="Discard Changes"
        description="Closing now will discard the unsaved ACR entry."
        isOpen={pendingClose !== null}
        onConfirm={confirmDiscard}
        setIsOpen={(open) => {
          if (!open) setPendingClose(null);
        }}
        title="Discard unsaved changes?"
      />
    </>
  );
};
